import useSWR from "swr";
import { useServicesContext } from "../../providers/ServicesProvider";
import { DeveloperSettings } from "../../domain/DeveloperSettings";

/**
 * Reads a single organization's developer settings as a ready-to-use {@link DeveloperSettings}
 * domain entity. Adaptation from the REST shape lives in the service (per the onion layering), so
 * consumers receive a typed domain value and never construct one themselves.
 *
 * Note the `org-developer-settings:` key prefix: the bulk `useDeveloperSettings` hook caches under
 * `developer-settings:<ids>` (a per-org map), so this single-org entry must not share that namespace.
 *
 * Cache caveat: this hits the same `GET /settings` endpoint as `useOrganizationSettings`, but under a
 * distinct SWR key (their cached value shapes differ — a `DeveloperSettings` entity here vs. the raw
 * `OrganizationSettings` there), so SWR will NOT deduplicate the two. Don't mount both for the same
 * org on one page; use whichever hook matches the data the page needs (this one for the developer tab).
 */
export const useOrganizationDeveloperSettings = ({ organizationId }: { organizationId?: string }) => {
    const { organizationSettingsService } = useServicesContext();

    const { data, error } = useSWR<DeveloperSettings, Error>(
        organizationId ? `org-developer-settings:${organizationId}` : null,
        () => organizationSettingsService!.getDeveloperSettings({ organizationId: organizationId! })
    );

    return {
        developerSettings: data,
        loading: !!organizationId && !error && !data,
        error
    };
};
